import { AfterViewInit, Directive, ElementRef, Input, OnChanges, Renderer2, SimpleChanges } from '@angular/core';

/**
 * TailwindMaterialDirective
 *
 * This directive allows applying Tailwind CSS classes to Angular Material components
 * by dynamically targeting the appropriate internal elements of the Material component.
 *
 * Usage:
 * <mat-form-field appTailwindMaterial [appTailwindMaterial]="['rounded-r-none', 'border-r-0']">
 *   <mat-select>...</mat-select>
 * </mat-form-field>
 */
@Directive({
    selector: '[appTailwindMaterial]',
    standalone: false,
})
export class TailwindMaterialDirective implements AfterViewInit, OnChanges {
    @Input() appTailwindMaterial: string[] = [];
    @Input() valueDependent: any;

    // Map of Tailwind class patterns to their corresponding Material element selectors
    private readonly classPatternMap = {
        // Border radius patterns for outline appearance
        rounded: ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-none': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-sm': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-md': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-lg': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-xl': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-2xl': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-3xl': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-full': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],

        // Directional border radius
        'rounded-t': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-t-none': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-t-sm': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-t-md': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-t-lg': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-t-xl': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-t-2xl': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-t-3xl': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-t-full': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],

        'rounded-r': '.mdc-notched-outline__trailing',
        'rounded-r-none': '.mdc-notched-outline__trailing',
        'rounded-r-sm': '.mdc-notched-outline__trailing',
        'rounded-r-md': '.mdc-notched-outline__trailing',
        'rounded-r-lg': '.mdc-notched-outline__trailing',
        'rounded-r-xl': '.mdc-notched-outline__trailing',
        'rounded-r-2xl': '.mdc-notched-outline__trailing',
        'rounded-r-3xl': '.mdc-notched-outline__trailing',
        'rounded-r-full': '.mdc-notched-outline__trailing',

        'rounded-b': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-b-none': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-b-sm': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-b-md': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-b-lg': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-b-xl': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-b-2xl': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-b-3xl': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'rounded-b-full': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],

        'rounded-l': '.mdc-notched-outline__leading',
        'rounded-l-none': '.mdc-notched-outline__leading',
        'rounded-l-sm': '.mdc-notched-outline__leading',
        'rounded-l-md': '.mdc-notched-outline__leading',
        'rounded-l-lg': '.mdc-notched-outline__leading',
        'rounded-l-xl': '.mdc-notched-outline__leading',
        'rounded-l-2xl': '.mdc-notched-outline__leading',
        'rounded-l-3xl': '.mdc-notched-outline__leading',
        'rounded-l-full': '.mdc-notched-outline__leading',

        'rounded-tl': '.mdc-notched-outline__leading',
        'rounded-tl-none': '.mdc-notched-outline__leading',
        'rounded-tl-sm': '.mdc-notched-outline__leading',
        'rounded-tl-md': '.mdc-notched-outline__leading',
        'rounded-tl-lg': '.mdc-notched-outline__leading',
        'rounded-tl-xl': '.mdc-notched-outline__leading',
        'rounded-tl-2xl': '.mdc-notched-outline__leading',
        'rounded-tl-3xl': '.mdc-notched-outline__leading',
        'rounded-tl-full': '.mdc-notched-outline__leading',

        'rounded-tr': '.mdc-notched-outline__trailing',
        'rounded-tr-none': '.mdc-notched-outline__trailing',
        'rounded-tr-sm': '.mdc-notched-outline__trailing',
        'rounded-tr-md': '.mdc-notched-outline__trailing',
        'rounded-tr-lg': '.mdc-notched-outline__trailing',
        'rounded-tr-xl': '.mdc-notched-outline__trailing',
        'rounded-tr-2xl': '.mdc-notched-outline__trailing',
        'rounded-tr-3xl': '.mdc-notched-outline__trailing',
        'rounded-tr-full': '.mdc-notched-outline__trailing',

        'rounded-bl': '.mdc-notched-outline__leading',
        'rounded-bl-none': '.mdc-notched-outline__leading',
        'rounded-bl-sm': '.mdc-notched-outline__leading',
        'rounded-bl-md': '.mdc-notched-outline__leading',
        'rounded-bl-lg': '.mdc-notched-outline__leading',
        'rounded-bl-xl': '.mdc-notched-outline__leading',
        'rounded-bl-2xl': '.mdc-notched-outline__leading',
        'rounded-bl-3xl': '.mdc-notched-outline__leading',
        'rounded-bl-full': '.mdc-notched-outline__leading',

        'rounded-br': '.mdc-notched-outline__trailing',
        'rounded-br-none': '.mdc-notched-outline__trailing',
        'rounded-br-sm': '.mdc-notched-outline__trailing',
        'rounded-br-md': '.mdc-notched-outline__trailing',
        'rounded-br-lg': '.mdc-notched-outline__trailing',
        'rounded-br-xl': '.mdc-notched-outline__trailing',
        'rounded-br-2xl': '.mdc-notched-outline__trailing',
        'rounded-br-3xl': '.mdc-notched-outline__trailing',
        'rounded-br-full': '.mdc-notched-outline__trailing',

        // Border width patterns for outline appearance
        border: ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-0': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-2': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-4': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-8': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],

        'border-t': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-t-0': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-t-2': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-t-4': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-t-8': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],

        'border-r': '.mdc-notched-outline__trailing',
        'border-r-0': '.mdc-notched-outline__trailing',
        'border-r-2': '.mdc-notched-outline__trailing',
        'border-r-4': '.mdc-notched-outline__trailing',
        'border-r-8': '.mdc-notched-outline__trailing',

        'border-b': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-b-0': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-b-2': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-b-4': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-b-8': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],

        'border-l': '.mdc-notched-outline__leading',
        'border-l-0': '.mdc-notched-outline__leading',
        'border-l-2': '.mdc-notched-outline__leading',
        'border-l-4': '.mdc-notched-outline__leading',
        'border-l-8': '.mdc-notched-outline__leading',

        // Border color patterns
        'border-transparent': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-current': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-black': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-white': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-gray': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-red': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-yellow': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-green': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-blue': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-indigo': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-purple': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-pink': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],

        // Border style patterns
        'border-solid': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-dashed': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-dotted': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-double': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],
        'border-none': ['.mdc-notched-outline__leading', '.mdc-notched-outline__notch', '.mdc-notched-outline__trailing'],

        // Border patterns for standard appearance
        'border-b-standard': '.mdc-line-ripple',

        // Width patterns
        'w-': '.mat-mdc-form-field-infix',
        'max-w-': '.mat-mdc-form-field-infix',

        // Height patterns
        'h-': '.mat-mdc-form-field-flex',

        // Padding patterns
        'p-': '.mat-mdc-form-field-infix',
        'px-': '.mat-mdc-form-field-infix',
        'py-': '.mat-mdc-form-field-infix',
        'pt-': '.mat-mdc-form-field-infix',
        'pr-': '.mat-mdc-form-field-infix',
        'pb-': '.mat-mdc-form-field-infix',
        'pl-': '.mat-mdc-form-field-infix',

        // Margin patterns
        'm-': '.mat-mdc-text-field-wrapper',
        'mx-': '.mat-mdc-text-field-wrapper',
        'my-': '.mat-mdc-text-field-wrapper',
        'mt-': '.mat-mdc-text-field-wrapper',
        'mr-': '.mat-mdc-text-field-wrapper',
        'mb-': '.mat-mdc-text-field-wrapper',
        'ml-': '.mat-mdc-text-field-wrapper',

        // Background patterns
        'bg-': '.mat-mdc-form-field-flex',

        // Text color patterns
        'text-': ['.mdc-floating-label', '.mat-mdc-select-placeholder', '.mat-mdc-select-value-text', '.mat-mdc-select-arrow'],
    };

    private readonly modifierMap = {
        none: '0',
        full: '9999px',
        sm: '0.125rem',
        md: '0.375rem',
        lg: '0.5rem',
        xl: '0.75rem',
        '2xl': '1rem',
        '3xl': '1.5rem',
    };

    constructor(private el: ElementRef, private renderer: Renderer2) {}

    ngAfterViewInit(): void {
        this.applyTailwindClasses();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.appTailwindMaterial && !changes.appTailwindMaterial.firstChange) {
            this.applyTailwindClasses();
        } else if (changes.valueDependent && !changes.valueDependent.firstChange) {
            setTimeout(() => {
                this.applyTailwindClasses();
            }, 100);
        }
    }

    private applyTailwindClasses(): void {
        if (!this.appTailwindMaterial || this.appTailwindMaterial.length === 0) return;

        // Get the appearance of the mat-form-field
        const element = this.el.nativeElement as HTMLElement;
        const isOutline = element.classList.contains('mat-form-field-appearance-outline');

        // Process each Tailwind class
        this.appTailwindMaterial.forEach(className => {
            // Handle important modifier
            const isImportant = className.startsWith('!');
            const actualClass = isImportant ? className.substring(1) : className;
            const importantModifier = isImportant ? ' !important' : '';

            // Special handling for border classes in outline form fields
            if (isOutline && actualClass.startsWith('border')) {
                this.applyBorderClassToOutlineElements(element, actualClass, importantModifier);
                return;
            }

            // Special handling for rounded classes in outline form fields
            if (isOutline && actualClass.startsWith('rounded')) {
                this.applyRoundedClassToOutlineElements(element, actualClass, importantModifier);
                return;
            }

            // Handle other classes using the pattern map
            const targetSelector = this.findTargetSelector(actualClass, isOutline);
            if (!targetSelector) return;

            // Handle array of selectors
            if (Array.isArray(targetSelector)) {
                // Process each selector in the array
                targetSelector.forEach(selector => {
                    const elements = element.querySelectorAll(selector);
                    if (elements && elements.length > 0) {
                        this.applyStyleToElements(elements, actualClass, isImportant);
                    }
                });
            } else {
                // Handle single selector
                const targetElements = element.querySelectorAll(targetSelector);
                if (!targetElements || targetElements.length === 0) return;

                // Apply the appropriate style based on the class
                this.applyStyleToElements(targetElements, actualClass, isImportant);
            }
        });
    }

    /**
     * Applies border-related Tailwind classes directly to the specific outline elements
     * of an Angular Material form field
     */
    private applyBorderClassToOutlineElements(element: HTMLElement, className: string, importantModifier: string): void {
        const parts = className.split('-');
        if (parts.length < 2) return;

        // Handle rounded-r-none and similar classes
        if (className.startsWith('rounded')) {
            this.applyRoundedClassToOutlineElements(element, className, importantModifier);
            return;
        }

        // Get all outline elements
        const outlines = element.querySelectorAll('.mdc-notched-outline');
        const outlineStarts = element.querySelectorAll('.mdc-notched-outline__leading');
        const outlineEnds = element.querySelectorAll('.mdc-notched-outline__trailing');
        const outlineGaps = element.querySelectorAll('.mdc-notched-outline__notch');

        // Handle border-0 (no border)
        if (parts[1] === '0') {
            outlines.forEach(el => this.renderer.setStyle(el, 'border', 'none' + importantModifier));
            outlineStarts.forEach(el => this.renderer.setStyle(el, 'border', 'none' + importantModifier));
            outlineEnds.forEach(el => this.renderer.setStyle(el, 'border', 'none' + importantModifier));
            outlineGaps.forEach(el => this.renderer.setStyle(el, 'border', 'none' + importantModifier));

            // Hide floating label when there's no border
            const floatingLabels = element.querySelectorAll('.mat-mdc-floating-label');
            floatingLabels.forEach(label => {
                this.renderer.setStyle(label, 'display', 'none' + importantModifier);
            });

            return;
        }

        // Handle border-{position}-0
        if (parts.length >= 3 && parts[2] === '0') {
            const position = parts[1];

            switch (position) {
                case 'r': // right border
                    // Apply to all outline elements
                    outlineStarts.forEach(el => {
                        this.renderer.setStyle(el, 'border-right', 'none' + importantModifier);
                    });

                    outlineEnds.forEach(el => {
                        this.renderer.setStyle(el, 'border-right', 'none' + importantModifier);
                        // Also remove left border to avoid double borders
                        this.renderer.setStyle(el, 'border-left', 'none' + importantModifier);
                    });

                    // Apply to the outline containers as well
                    outlines.forEach(el => {
                        this.renderer.setStyle(el, 'border-right', 'none' + importantModifier);
                    });
                    break;

                case 'l': // left border
                    outlineStarts.forEach(el => {
                        this.renderer.setStyle(el, 'border-left', 'none' + importantModifier);
                    });

                    outlineEnds.forEach(el => {
                        this.renderer.setStyle(el, 'border-left', 'none' + importantModifier);
                    });

                    outlines.forEach(el => {
                        this.renderer.setStyle(el, 'border-left', 'none' + importantModifier);
                    });
                    break;

                case 't': // top border
                    outlineStarts.forEach(el => {
                        this.renderer.setStyle(el, 'border-top', 'none' + importantModifier);
                    });

                    outlineGaps.forEach(el => {
                        this.renderer.setStyle(el, 'border-top', 'none' + importantModifier);
                    });

                    outlineEnds.forEach(el => {
                        this.renderer.setStyle(el, 'border-top', 'none' + importantModifier);
                    });

                    outlines.forEach(el => {
                        this.renderer.setStyle(el, 'border-top', 'none' + importantModifier);
                    });
                    break;

                case 'b': // bottom border
                    outlineStarts.forEach(el => {
                        this.renderer.setStyle(el, 'border-bottom', 'none' + importantModifier);
                    });

                    outlineGaps.forEach(el => {
                        this.renderer.setStyle(el, 'border-bottom', 'none' + importantModifier);
                    });

                    outlineEnds.forEach(el => {
                        this.renderer.setStyle(el, 'border-bottom', 'none' + importantModifier);
                    });

                    outlines.forEach(el => {
                        this.renderer.setStyle(el, 'border-bottom', 'none' + importantModifier);
                    });
                    break;
            }
        }
    }

    /**
     * Applies rounded-related Tailwind classes to the appropriate elements
     */
    private applyRoundedClassToOutlineElements(element: HTMLElement, className: string, importantModifier: string): void {
        // Get all relevant elements
        const outlines = element.querySelectorAll('.mdc-notched-outline');
        const outlineStarts = element.querySelectorAll('.mdc-notched-outline__leading');
        const outlineEnds = element.querySelectorAll('.mdc-notched-outline__trailing');

        // Handle different rounded classes
        if (className === 'rounded-r-none' || className === 'rounded-tr-none' || className === 'rounded-br-none') {
            // Apply to all outline elements
            outlines.forEach(el => {
                this.renderer.setStyle(el, 'border-top-right-radius', '0' + importantModifier);
                this.renderer.setStyle(el, 'border-bottom-right-radius', '0' + importantModifier);
            });

            // Apply specifically to end parts
            outlineEnds.forEach(el => {
                this.renderer.setStyle(el, 'border-top-right-radius', '0' + importantModifier);
                this.renderer.setStyle(el, 'border-bottom-right-radius', '0' + importantModifier);
            });
        } else if (className === 'rounded-l-none' || className === 'rounded-tl-none' || className === 'rounded-bl-none') {
            // Apply to all outline elements
            outlines.forEach(el => {
                this.renderer.setStyle(el, 'border-top-left-radius', '0' + importantModifier);
                this.renderer.setStyle(el, 'border-bottom-left-radius', '0' + importantModifier);
            });

            // Apply specifically to start parts
            outlineStarts.forEach(el => {
                this.renderer.setStyle(el, 'border-top-left-radius', '0' + importantModifier);
                this.renderer.setStyle(el, 'border-bottom-left-radius', '0' + importantModifier);
            });
        } else {
            // For other rounded classes, use the general border radius handler
            // Apply border radius to outline elements instead
            outlines.forEach(el => {
                this.applyBorderRadiusStyle(el, className, importantModifier);
            });

            // Also apply to start and end elements for complete coverage
            outlineStarts.forEach(el => {
                this.applyBorderRadiusStyle(el, className, importantModifier);
            });

            outlineEnds.forEach(el => {
                this.applyBorderRadiusStyle(el, className, importantModifier);
            });
        }
    }

    private findTargetSelector(className: string, isOutline: boolean): string | string[] | null {
        // Check for exact matches first
        for (const [pattern, selector] of Object.entries(this.classPatternMap)) {
            if (className === pattern) {
                return selector;
            }
        }

        // Handle border-b for standard appearance
        if (className === 'border-b' && !isOutline) {
            return this.classPatternMap['border-b-standard'];
        }

        // Check for pattern matches
        for (const [pattern, selector] of Object.entries(this.classPatternMap)) {
            if (className.startsWith(pattern)) {
                return selector;
            }
        }

        return null;
    }

    private applyStyleToElements(elements: NodeListOf<Element>, className: string, isImportant: boolean): void {
        const importantModifier = isImportant ? ' !important' : '';

        elements.forEach(element => {
            // Handle border radius classes
            if (className.startsWith('rounded')) {
                this.applyBorderRadiusStyle(element, className, importantModifier);
            }
            // Handle border classes for outline appearance
            else if (className.startsWith('border')) {
                this.applyBorderStyle(element, className, importantModifier);
            }
            // Handle width classes
            else if (className.startsWith('w-')) {
                this.applyWidthStyle(element, className, importantModifier);
            }
            // Handle max-width classes
            else if (className.startsWith('max-w-')) {
                this.applyMaxWidthStyle(element, className, importantModifier);
            }
            // Handle height classes
            else if (className.startsWith('h-')) {
                this.applyHeightStyle(element, className, importantModifier);
            }
            // Handle background classes
            else if (className.startsWith('bg-')) {
                this.applyBackgroundStyle(element, className, importantModifier);
            }
            // Handle text color classes
            else if (className.startsWith('text-')) {
                this.applyTextColorStyle(element, className, importantModifier);
            }
            // Handle padding classes
            else if (className.startsWith('p')) {
                this.applyPaddingStyle(element, className, importantModifier);
            }
            // Handle margin classes
            else if (className.startsWith('m')) {
                this.applyMarginStyle(element, className, importantModifier);
            }
        });
    }

    private applyBorderRadiusStyle(element: Element, className: string, importantModifier: string): void {
        // Handle arbitrary values with square brackets like rounded-[10px] or rounded-t-[calc(100%-5px)]
        const arbitraryValueMatch = className.match(/rounded(-[trblxy])?(-[a-z]+)?-\[([^\]]+)\]/);
        if (arbitraryValueMatch) {
            const direction = arbitraryValueMatch[1] ? arbitraryValueMatch[1].substring(1) : null;
            const corner = arbitraryValueMatch[2] ? arbitraryValueMatch[2].substring(1) : null;
            const value = arbitraryValueMatch[3];

            // Check if it's a negative value (starts with -)
            const isNegative = className.startsWith('-');
            const actualValue = isNegative ? '-' + value : value;

            this.applyBorderRadiusWithValue(element, direction, corner, actualValue, importantModifier);
            return;
        }

        const parts = className.split('-');

        // Check if it's a negative value
        const isNegative = parts[0] === '' && parts[1] === 'rounded';
        const startIndex = isNegative ? 1 : 0;

        // Handle rounded with no suffix (all corners)
        if (parts.length === startIndex + 1) {
            const value = isNegative ? '-0.25rem' : '0.25rem';
            this.renderer.setStyle(element, 'border-radius', value + importantModifier);
            return;
        }

        // Handle rounded-{size} (all corners with specific size)
        if (parts.length === startIndex + 2) {
            const size = parts[startIndex + 1];
            let value = this.modifierMap[size] || size;
            if (isNegative) value = '-' + value;
            this.renderer.setStyle(element, 'border-radius', value + importantModifier);
            return;
        }

        // Handle rounded-{position}-{size}
        if (parts.length === startIndex + 3) {
            const position = parts[startIndex + 1];
            const size = parts[startIndex + 2];
            let value = this.modifierMap[size] || size;
            if (isNegative) value = '-' + value;

            switch (position) {
                case 'r': // right
                    this.renderer.setStyle(element, 'border-top-right-radius', value + importantModifier);
                    this.renderer.setStyle(element, 'border-bottom-right-radius', value + importantModifier);
                    break;
                case 'l': // left
                    this.renderer.setStyle(element, 'border-top-left-radius', value + importantModifier);
                    this.renderer.setStyle(element, 'border-bottom-left-radius', value + importantModifier);
                    break;
                case 't': // top
                    this.renderer.setStyle(element, 'border-top-left-radius', value + importantModifier);
                    this.renderer.setStyle(element, 'border-top-right-radius', value + importantModifier);
                    break;
                case 'b': // bottom
                    this.renderer.setStyle(element, 'border-bottom-left-radius', value + importantModifier);
                    this.renderer.setStyle(element, 'border-bottom-right-radius', value + importantModifier);
                    break;
                case 'tl': // top-left
                    this.renderer.setStyle(element, 'border-top-left-radius', value + importantModifier);
                    break;
                case 'tr': // top-right
                    this.renderer.setStyle(element, 'border-top-right-radius', value + importantModifier);
                    break;
                case 'bl': // bottom-left
                    this.renderer.setStyle(element, 'border-bottom-left-radius', value + importantModifier);
                    break;
                case 'br': // bottom-right
                    this.renderer.setStyle(element, 'border-bottom-right-radius', value + importantModifier);
                    break;
            }
        }
    }

    private applyBorderStyle(element: Element, className: string, importantModifier: string): void {
        // Handle arbitrary values with square brackets like border-[1px] or border-t-[calc(1px+2px)]
        const arbitraryValueMatch = className.match(/border(-[trbl])?-\[([^\]]+)\]/);
        if (arbitraryValueMatch) {
            const direction = arbitraryValueMatch[1] ? arbitraryValueMatch[1].substring(1) : null;
            const value = arbitraryValueMatch[2];

            // Check if it's a negative value (starts with -)
            const isNegative = className.startsWith('-');
            const actualValue = isNegative ? '-' + value : value;

            this.applyBorderWithValue(element, direction, actualValue, importantModifier);
            return;
        }

        // Handle border with no suffix (all borders)
        const parts = className.split('-');
        if (parts.length === 1) {
            this.renderer.setStyle(element, 'border', '1px solid rgba(0, 0, 0, 0.12)' + importantModifier);
            return;
        }

        // Handle more specific border cases for Material outline parts
        if (parts.length >= 3 && parts[0] === 'border' && (parts[2] === 'start' || parts[2] === 'end')) {
            const position = parts[1];
            const part = parts[2];

            if (parts.length === 4 && parts[3] === '0') {
                // Handle border-{position}-{part}-0
                switch (position) {
                    case 'r': // right
                        if (part === 'end') {
                            this.renderer.setStyle(element, 'border-right', 'none' + importantModifier);
                        }
                        break;
                    case 'l': // left
                        if (part === 'start') {
                            this.renderer.setStyle(element, 'border-left', 'none' + importantModifier);
                        } else {
                            this.renderer.setStyle(element, 'border-left', 'none' + importantModifier);
                        }
                        break;
                    case 't': // top
                        this.renderer.setStyle(element, 'border-top', 'none' + importantModifier);
                        break;
                    case 'b': // bottom
                        this.renderer.setStyle(element, 'border-bottom', 'none' + importantModifier);
                        break;
                }
            }
        }
    }

    private applyWidthStyle(element: Element, className: string, importantModifier: string): void {
        const parts = className.split('-');
        if (parts.length < 2) return;

        const size = parts[1];

        // Handle special cases
        if (size === 'full') {
            this.renderer.setStyle(element, 'width', '100%' + importantModifier);
        } else if (size === 'auto') {
            this.renderer.setStyle(element, 'width', 'auto' + importantModifier);
        } else if (size === 'screen') {
            this.renderer.setStyle(element, 'width', '100vw' + importantModifier);
        } else if (!isNaN(Number(size))) {
            // Handle numeric values (w-1, w-2, etc.)
            this.renderer.setStyle(element, 'width', Number(size) * 0.25 + 'rem' + importantModifier);
        } else if (size.includes('[')) {
            const value = size.replace('[', '').replace(']', '');
            this.renderer.setStyle(element, 'width', value + importantModifier);
        } else {
            // Handle other values (could be custom values like w-1/2)
            // For fractions like w-1/2
            if (size.includes('/')) {
                const [numerator, denominator] = size.split('/');
                const percentage = (Number(numerator) / Number(denominator)) * 100;
                this.renderer.setStyle(element, 'width', percentage + '%' + importantModifier);
            }
        }
    }

    private applyMaxWidthStyle(element: Element, className: string, importantModifier: string): void {
        const parts = className.split('-');
        if (parts.length < 2) return;

        const size = parts[1];

        // Handle special cases
        if (size === 'full') {
            this.renderer.setStyle(element, 'max-width', '100%' + importantModifier);
        } else if (size === 'auto') {
            this.renderer.setStyle(element, 'max-width', 'auto' + importantModifier);
        } else if (size === 'screen') {
            this.renderer.setStyle(element, 'max-width', '100vw' + importantModifier);
        } else if (!isNaN(Number(size))) {
            // Handle numeric values (w-1, w-2, etc.)
            this.renderer.setStyle(element, 'max-width', Number(size) * 0.25 + 'rem' + importantModifier);
        } else if (size.includes('[')) {
            const value = size.replace('[', '').replace(']', '');
            this.renderer.setStyle(element, 'max-width', value + importantModifier);
        } else {
            // Handle other values (could be custom values like w-1/2)
            // For fractions like w-1/2
            if (size.includes('/')) {
                const [numerator, denominator] = size.split('/');
                const percentage = (Number(numerator) / Number(denominator)) * 100;
                this.renderer.setStyle(element, 'max-width', percentage + '%' + importantModifier);
            }
        }
    }

    private applyHeightStyle(element: Element, className: string, importantModifier: string): void {
        const parts = className.split('-');
        if (parts.length < 2) return;

        const size = parts[1];

        // Handle special cases
        if (size === 'full') {
            this.renderer.setStyle(element, 'height', '100%' + importantModifier);
        } else if (size === 'auto') {
            this.renderer.setStyle(element, 'height', 'auto' + importantModifier);
        } else if (size === 'screen') {
            this.renderer.setStyle(element, 'height', '100vh' + importantModifier);
        } else if (!isNaN(Number(size))) {
            // Handle numeric values (h-1, h-2, etc.)
            this.renderer.setStyle(element, 'height', Number(size) * 0.25 + 'rem' + importantModifier);
        } else {
            // Handle other values (could be custom values like h-1/2)
            // For fractions like h-1/2
            if (size.includes('/')) {
                const [numerator, denominator] = size.split('/');
                const percentage = (Number(numerator) / Number(denominator)) * 100;
                this.renderer.setStyle(element, 'height', percentage + '%' + importantModifier);
            }
        }
    }

    private applyBackgroundStyle(element: Element, className: string, importantModifier: string): void {
        // Enhanced implementation to handle Tailwind background colors with shades
        const parts = className.split('-');
        if (parts.length < 2) return;

        // Base color map for common colors
        const colorMap = {
            transparent: 'transparent',
            white: '#ffffff',
            black: '#000000',
            taxillaGreen: '#00a651',
        };

        // Color shade maps for common colors
        const colorShadeMap = {
            gray: {
                50: '#f9fafb',
                100: '#f3f4f6',
                200: '#e5e7eb',
                300: '#d1d5db',
                400: '#9ca3af',
                500: '#6b7280',
                600: '#4b5563',
                700: '#374151',
                800: '#1f2937',
                900: '#111827',
            },
            red: {
                50: '#fef2f2',
                100: '#fee2e2',
                200: '#fecaca',
                300: '#fca5a5',
                400: '#f87171',
                500: '#ef4444',
                600: '#dc2626',
                700: '#b91c1c',
                800: '#991b1b',
                900: '#7f1d1d',
            },
            green: {
                50: '#f0fdf4',
                100: '#dcfce7',
                200: '#bbf7d0',
                300: '#86efac',
                400: '#4ade80',
                500: '#22c55e',
                600: '#16a34a',
                700: '#15803d',
                800: '#166534',
                900: '#14532d',
            },
            blue: {
                50: '#eff6ff',
                100: '#dbeafe',
                200: '#bfdbfe',
                300: '#93c5fd',
                400: '#60a5fa',
                500: '#3b82f6',
                600: '#2563eb',
                700: '#1d4ed8',
                800: '#1e40af',
                900: '#1e3a8a',
            },
        };

        const color = parts[1];
        const shade = parts.length > 2 ? parts[2] : null;

        // Handle base colors
        if (color in colorMap) {
            this.renderer.setStyle(element, 'background-color', colorMap[color] + importantModifier);
            return;
        }

        // Handle colors with shades
        if (color in colorShadeMap && shade && colorShadeMap[color][shade]) {
            this.renderer.setStyle(element, 'background-color', colorShadeMap[color][shade] + importantModifier);
            return;
        }
    }

    private applyTextColorStyle(element: Element, className: string, importantModifier: string): void {
        // Enhanced implementation to handle Tailwind text colors with shades
        const parts = className.split('-');
        if (parts.length < 2) return;

        // Base color map for common colors
        const colorMap = {
            transparent: 'transparent',
            white: '#ffffff',
            black: '#000000',
            taxillaGreen: '#00a651',
        };

        // Color shade maps for common colors
        const colorShadeMap = {
            gray: {
                50: '#f9fafb',
                100: '#f3f4f6',
                200: '#e5e7eb',
                300: '#d1d5db',
                400: '#9ca3af',
                500: '#6b7280',
                600: '#4b5563',
                700: '#374151',
                800: '#1f2937',
                900: '#111827',
            },
            red: {
                50: '#fef2f2',
                100: '#fee2e2',
                200: '#fecaca',
                300: '#fca5a5',
                400: '#f87171',
                500: '#ef4444',
                600: '#dc2626',
                700: '#b91c1c',
                800: '#991b1b',
                900: '#7f1d1d',
            },
            green: {
                50: '#f0fdf4',
                100: '#dcfce7',
                200: '#bbf7d0',
                300: '#86efac',
                400: '#4ade80',
                500: '#22c55e',
                600: '#16a34a',
                700: '#15803d',
                800: '#166534',
                900: '#14532d',
            },
            blue: {
                50: '#eff6ff',
                100: '#dbeafe',
                200: '#bfdbfe',
                300: '#93c5fd',
                400: '#60a5fa',
                500: '#3b82f6',
                600: '#2563eb',
                700: '#1d4ed8',
                800: '#1e40af',
                900: '#1e3a8a',
            },
        };

        const color = parts[1];
        const shade = parts.length > 2 ? parts[2] : null;

        // Handle base colors
        if (color in colorMap) {
            this.renderer.setStyle(element, 'color', colorMap[color] + importantModifier);
            return;
        }

        // Handle colors with shades
        if (color in colorShadeMap && shade && colorShadeMap[color][shade]) {
            this.renderer.setStyle(element, 'color', colorShadeMap[color][shade] + importantModifier);
            return;
        }
    }

    private applyPaddingStyle(element: Element, className: string, importantModifier: string): void {
        const parts = className.split('-');
        if (parts.length < 2) return;

        const direction = parts[0];
        const size = parts[1];

        if (!isNaN(Number(size))) {
            const value = Number(size) * 0.25 + 'rem' + importantModifier;

            switch (direction) {
                case 'p': // padding all sides
                    this.renderer.setStyle(element, 'padding', value);
                    break;
                case 'px': // padding x-axis (left and right)
                    this.renderer.setStyle(element, 'padding-left', value);
                    this.renderer.setStyle(element, 'padding-right', value);
                    break;
                case 'py': // padding y-axis (top and bottom)
                    this.renderer.setStyle(element, 'padding-top', value);
                    this.renderer.setStyle(element, 'padding-bottom', value);
                    break;
                case 'pt': // padding top
                    this.renderer.setStyle(element, 'padding-top', value);
                    break;
                case 'pr': // padding right
                    this.renderer.setStyle(element, 'padding-right', value);
                    break;
                case 'pb': // padding bottom
                    this.renderer.setStyle(element, 'padding-bottom', value);
                    break;
                case 'pl': // padding left
                    this.renderer.setStyle(element, 'padding-left', value);
                    break;
            }
        }
    }

    private applyMarginStyle(element: Element, className: string, importantModifier: string): void {
        const parts = className.split('-');
        if (parts.length < 2) return;

        const direction = parts[0];
        const size = parts[1];

        if (!isNaN(Number(size))) {
            const value = Number(size) * 0.25 + 'rem' + importantModifier;

            switch (direction) {
                case 'm': // margin all sides
                    this.renderer.setStyle(element, 'margin', value);
                    break;
                case 'mx': // margin x-axis (left and right)
                    this.renderer.setStyle(element, 'margin-left', value);
                    this.renderer.setStyle(element, 'margin-right', value);
                    break;
                case 'my': // margin y-axis (top and bottom)
                    this.renderer.setStyle(element, 'margin-top', value);
                    this.renderer.setStyle(element, 'margin-bottom', value);
                    break;
                case 'mt': // margin top
                    this.renderer.setStyle(element, 'margin-top', value);
                    break;
                case 'mr': // margin right
                    this.renderer.setStyle(element, 'margin-right', value);
                    break;
                case 'mb': // margin bottom
                    this.renderer.setStyle(element, 'margin-bottom', value);
                    break;
                case 'ml': // margin left
                    this.renderer.setStyle(element, 'margin-left', value);
                    break;
            }
        }
    }

    /**
     * Applies border radius with arbitrary values from square bracket notation
     * Handles cases like rounded-[10px], rounded-t-[5px], or -rounded-tr-[8px]
     */
    private applyBorderRadiusWithValue(
        element: Element,
        direction: string | null,
        corner: string | null,
        value: string,
        importantModifier: string
    ): void {
        // Handle all corners
        if (!direction && !corner) {
            this.renderer.setStyle(element, 'border-radius', value + importantModifier);
            return;
        }

        // Handle directional borders
        switch (direction) {
            case 'r': // right
                this.renderer.setStyle(element, 'border-top-right-radius', value + importantModifier);
                this.renderer.setStyle(element, 'border-bottom-right-radius', value + importantModifier);
                break;
            case 'l': // left
                this.renderer.setStyle(element, 'border-top-left-radius', value + importantModifier);
                this.renderer.setStyle(element, 'border-bottom-left-radius', value + importantModifier);
                break;
            case 't': // top
                this.renderer.setStyle(element, 'border-top-left-radius', value + importantModifier);
                this.renderer.setStyle(element, 'border-top-right-radius', value + importantModifier);
                break;
            case 'b': // bottom
                this.renderer.setStyle(element, 'border-bottom-left-radius', value + importantModifier);
                this.renderer.setStyle(element, 'border-bottom-right-radius', value + importantModifier);
                break;
        }

        // Handle specific corners
        if (corner) {
            switch (corner) {
                case 'tl': // top-left
                    this.renderer.setStyle(element, 'border-top-left-radius', value + importantModifier);
                    break;
                case 'tr': // top-right
                    this.renderer.setStyle(element, 'border-top-right-radius', value + importantModifier);
                    break;
                case 'bl': // bottom-left
                    this.renderer.setStyle(element, 'border-bottom-left-radius', value + importantModifier);
                    break;
                case 'br': // bottom-right
                    this.renderer.setStyle(element, 'border-bottom-right-radius', value + importantModifier);
                    break;
            }
        }
    }

    /**
     * Helper method to apply border styles with arbitrary values
     */
    private applyBorderWithValue(element: Element, direction: string | null, value: string, importantModifier: string): void {
        if (!direction) {
            // Apply to all borders
            this.renderer.setStyle(element, 'border-width', value + importantModifier);
            return;
        }

        switch (direction) {
            case 't': // top
                this.renderer.setStyle(element, 'border-top-width', value + importantModifier);
                break;
            case 'r': // right
                this.renderer.setStyle(element, 'border-right-width', value + importantModifier);
                break;
            case 'b': // bottom
                this.renderer.setStyle(element, 'border-bottom-width', value + importantModifier);
                break;
            case 'l': // left
                this.renderer.setStyle(element, 'border-left-width', value + importantModifier);
                break;
        }
    }
}
